import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";

import "./BonusHistory.css";


/* =========================================================
   FIREBASE COLLECTIONS
========================================================= */

const TSH_COLLECTION =
  "tsh_transactions";

const VISITOR_COLLECTION =
  "visterdeposte";

const USDT_COLLECTION =
  "usdt_transactions";


/* =========================================================
   LOCAL STORAGE KEYS
========================================================= */

const LOCAL_STORAGE_KEYS = [
  "username",
  "user",
  "currentUser",
  "loggedInUser",
  "userData",
];


/* =========================================================
   COUNTRY → CURRENCY
========================================================= */

const countryCurrency = {

  Tanzania:
    "TZS",

  Kenya:
    "KES",

  Uganda:
    "UGX",

  Rwanda:
    "RWF",

  Burundi:
    "BIF",

  "DRC - Congo":
    "USD",

  Zambia:
    "ZMW",

  Malawi:
    "MWK",

  Mozambique:
    "MZN",

  Zimbabwe:
    "USD",

  "South Sudan":
    "SSP",

  Botswana:
    "BWP",

  Madagascar:
    "MGA",

};


/* =========================================================
   CURRENCY → TZS RATE
========================================================= */

const currencyRates = {

  Kenya:
    21.5,

  Uganda:
    0.77,

  Rwanda:
    1.9,

  Burundi:
    0.94,

  "DRC - Congo":
    2762.43,

  Zambia:
    147.23,

  Malawi:
    1.382,

  Mozambique:
    43.71,

  Zimbabwe:
    2762.43,

  "South Sudan":
    0.469,

  Botswana:
    211.95,

  Madagascar:
    0.684,

};


/* =========================================================
   TEXT NORMALIZER
========================================================= */

const normalizeText = (
  value
) => {

  return String(
    value || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   STATUS NORMALIZER
========================================================= */

const normalizeStatus = (
  value
) => {

  return String(
    value || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   NUMBER
========================================================= */

const toNumber = (
  value
) => {

  const number =
    Number(
      value
    );

  return Number.isFinite(
    number
  )
    ? number
    : 0;

};


/* =========================================================
   GET TIMESTAMP SECONDS
========================================================= */

const getTimestampSeconds = (
  item
) => {

  const value =
    item?.referralBonusClaimedAt ||
    item?.selfBonusApprovedAt ||
    item?.paidAt ||
    item?.updatedAt ||
    item?.createdAt ||
    item?.timestamp;


  if (
    !value
  ) {

    return 0;

  }


  /* Firestore Timestamp */

  if (
    typeof value?.seconds ===
    "number"
  ) {

    return Number(
      value.seconds
    );

  }


  /* Firestore Timestamp with toDate() */

  if (
    typeof value?.toDate ===
    "function"
  ) {

    try {

      const date =
        value.toDate();


      if (
        date instanceof Date &&
        !Number.isNaN(
          date.getTime()
        )
      ) {

        return Math.floor(
          date.getTime() /
          1000
        );

      }

    }

    catch {

      return 0;

    }

  }


  /* Number timestamp */

  if (
    typeof value ===
    "number"
  ) {

    return value >
      100000000000

      ? Math.floor(
          value / 1000
        )

      : Math.floor(
          value
        );

  }


  /* String date */

  const parsed =
    new Date(
      value
    ).getTime();


  if (
    Number.isNaN(
      parsed
    )
  ) {

    return 0;

  }


  return Math.floor(
    parsed /
    1000
  );

};


/* =========================================================
   FORMAT MONEY
========================================================= */

const formatMoney = (
  amount,
  currency = "TZS"
) => {

  const value =
    toNumber(
      amount
    );


  return `${value.toLocaleString()} ${currency}`;

};


/* =========================================================
   FORMAT DATE
========================================================= */

const formatDate = (
  seconds
) => {

  if (
    !seconds
  ) {

    return "-";

  }


  try {

    return new Date(
      Number(
        seconds
      ) *
      1000
    ).toLocaleString();

  }

  catch {

    return "-";

  }

};


/* =========================================================
   GET USERNAME FROM LOCAL STORAGE
========================================================= */

const getUsernameFromLocalStorage = () => {

  /*
    DIRECT USERNAME
  */

  const directUsername =
    localStorage.getItem(
      "username"
    );


  if (
    directUsername &&
    directUsername.trim()
  ) {

    return directUsername.trim();

  }


  /*
    OTHER POSSIBLE KEYS
  */

  for (
    const key of
    LOCAL_STORAGE_KEYS
  ) {

    const stored =
      localStorage.getItem(
        key
      );


    if (
      !stored
    ) {

      continue;

    }


    /*
      TRY JSON
    */

    try {

      const parsed =
        JSON.parse(
          stored
        );


      /*
        JSON OBJECT
      */

      if (
        parsed &&
        typeof parsed ===
        "object"
      ) {

        const possibleUsername =

          parsed.username ||

          parsed.userName ||

          parsed.user_name ||

          parsed.name ||

          parsed.user?.username ||

          "";


        if (
          possibleUsername &&
          String(
            possibleUsername
          ).trim()
        ) {

          return String(
            possibleUsername
          ).trim();

        }

      }


      /*
        JSON STRING
      */

      if (
        typeof parsed ===
        "string"
      ) {

        if (
          parsed.trim()
        ) {

          return parsed.trim();

        }

      }

    }

    catch {

      /*
        NORMAL STRING
      */

      if (
        stored.trim()
      ) {

        return stored.trim();

      }

    }

  }


  return "";

};


/* =========================================================
   GET COUNTRY CURRENCY
========================================================= */

const getCurrencyFromCountry = (
  country
) => {

  return (
    countryCurrency[
      country
    ] || ""
  );

};


/* =========================================================
   GET TRANSACTION CURRENCY
========================================================= */

const getTransactionCurrency = (
  item
) => {

  /*
    TSH COLLECTION
    = TZS
  */

  if (
    item?.source ===
    TSH_COLLECTION
  ) {

    return "TZS";

  }


  /*
    USDT COLLECTION
    = USDT
  */

  if (
    item?.source ===
    USDT_COLLECTION
  ) {

    return "USDT";

  }


  /*
    Existing bonus currency
  */

  const explicitCurrency =

    item?.referralBonusCurrency ||

    item?.selfBonusCurrency ||

    item?.bonusCurrency ||

    item?.currency ||

    item?.sourceCurrency;


  if (
    explicitCurrency
  ) {

    return String(
      explicitCurrency
    )
      .trim()
      .toUpperCase();

  }


  /*
    Country fallback
  */

  return (
    getCurrencyFromCountry(
      item?.country
    ) || "TZS"
  );

};


/* =========================================================
   GET DEPOSIT AMOUNT
========================================================= */

const getDepositAmount = (
  item
) => {

  return toNumber(
    item?.bonusDepositAmount ??
    item?.amount ??
    item?.originalAmount ??
    item?.depositAmount ??
    item?.deposit ??
    0
  );

};


/* =========================================================
   GET SELF BONUS AMOUNT
========================================================= */

const getSelfBonusAmount = (
  item
) => {

  const amount =
    toNumber(
      item?.selfBonusClaimedAmount ??
      item?.selfBonusAmount ??
      item?.selfBonus ??
      0
    );


  return amount;

};


/* =========================================================
   GET REFERRAL BONUS AMOUNT
========================================================= */

const getReferralBonusAmount = (
  item
) => {

  /*
    IMPORTANT:

    For USDT:
    referralBonusClaimedAmount = 8

    For TZS:
    referralBonusClaimedAmount = 12000
  */

  const nativeAmount =
    toNumber(
      item?.referralBonusClaimedAmount ??
      item?.referralBonusAmount ??
      item?.referralBonus ??
      0
    );


  if (
    nativeAmount > 0
  ) {

    return nativeAmount;

  }


  /*
    Old TZS records
  */

  const tzsAmount =
    toNumber(
      item?.referralBonusClaimedAmountTZS ??
      item?.referralBonusAmountTZS ??
      0
    );


  return tzsAmount;

};


/* =========================================================
   GET REFERRAL BONUS CURRENCY
========================================================= */

const getReferralBonusCurrency = (
  item
) => {

  /*
    USDT COLLECTION
  */

  if (
    item?.source ===
    USDT_COLLECTION
  ) {

    return "USDT";

  }


  /*
    Explicit referral currency
  */

  if (
    item?.referralBonusCurrency
  ) {

    return String(
      item.referralBonusCurrency
    )
      .trim()
      .toUpperCase();

  }


  /*
    Native referral amount
  */

  const nativeAmount =
    toNumber(
      item?.referralBonusClaimedAmount ??
      item?.referralBonusAmount ??
      item?.referralBonus ??
      0
    );


  if (
    nativeAmount > 0
  ) {

    return getTransactionCurrency(
      item
    );

  }


  /*
    TZS fallback
  */

  if (
    toNumber(
      item?.referralBonusClaimedAmountTZS ??
      item?.referralBonusAmountTZS ??
      0
    ) > 0
  ) {

    return "TZS";

  }


  return getTransactionCurrency(
    item
  );

};


/* =========================================================
   GET BONUS STATUS
========================================================= */

const getBonusStatus = (
  item
) => {

  const status =
    normalizeStatus(
      item?.status
    );


  if (
    status === "success" ||
    status === "successful" ||
    status === "paid" ||
    status === "completed" ||
    status === "complete"
  ) {

    return "Paid";

  }


  if (
    status === "pending" ||
    status === "processing"
  ) {

    return "Pending";

  }


  if (
    status === "failed" ||
    status === "cancelled" ||
    status === "canceled"
  ) {

    return "Failed";

  }


  /*
    Bonus flags
  */

  if (
    item?.selfBonusGiven === true ||
    item?.referralBonusGiven === true
  ) {

    return "Paid";

  }


  return (
    item?.status ||
    "Pending"
  );

};


/* =========================================================
   CHECK IF USER IS REFERRAL RECEIVER
========================================================= */

const isUserReferralReceiver = (
  item,
  currentUsername
) => {

  const username =
    normalizeText(
      currentUsername
    );


  if (
    !username
  ) {

    return false;

  }


  /*
    NEW / CORRECT FIELD

    Example:

    referralBonusReceiver:
    "Pigiment21"

  */

  const referralReceiver =
    normalizeText(
      item?.referralBonusReceiver
    );


  if (
    referralReceiver &&
    referralReceiver ===
    username
  ) {

    return true;

  }


  /*
    OLD FIELD

    Example:

    referralBy:
    "Pigiment21"
  */

  const referralBy =
    normalizeText(
      item?.referralBy
    );


  if (
    referralBy &&
    referralBy ===
    username
  ) {

    return true;

  }


  return false;

};


/* =========================================================
   COMPONENT
========================================================= */

export default function BonusHistory() {

  /* =======================================================
     USERNAME
  ======================================================= */

  const [
    username,
    setUsername,
  ] = useState("");


  /* =======================================================
     BONUS LIST
  ======================================================= */

  const [
    bonusList,
    setBonusList,
  ] = useState([]);


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    loading,
    setLoading,
  ] = useState(true);


  /* =======================================================
     ERROR
  ======================================================= */

  const [
    error,
    setError,
  ] = useState("");


  /* =======================================================
     OPEN DETAILS
  ======================================================= */

  const [
    openId,
    setOpenId,
  ] = useState(null);


  /* =======================================================
     LOAD USERNAME
  ======================================================= */

  const loadUsername =
    useCallback(
      () => {

        const storedUsername =
          getUsernameFromLocalStorage();


        setUsername(
          storedUsername
        );

      },
      []
    );


  /* =======================================================
     INITIAL USERNAME
  ======================================================= */

  useEffect(
    () => {

      loadUsername();

    },
    [
      loadUsername,
    ]
  );


  /* =======================================================
     FETCH BONUS HISTORY
  ======================================================= */

  const fetchBonusHistory =
    useCallback(
      async () => {

        /*
          USERNAME REQUIRED
        */

        if (
          !username ||
          !String(
            username
          ).trim()
        ) {

          setBonusList([]);

          setLoading(
            false
          );

          setError(
            "Username haikupatikana."
          );

          return;

        }


        setLoading(
          true
        );


        setError("");


        try {

          const currentUsername =
            normalizeText(
              username
            );


          /* =================================================
             OWN DEPOSIT QUERIES
          ================================================= */

          const tshOwnQuery =
            query(
              collection(
                db,
                TSH_COLLECTION
              ),
              where(
                "username",
                "==",
                username
              )
            );


          const visitorOwnQuery =
            query(
              collection(
                db,
                VISITOR_COLLECTION
              ),
              where(
                "username",
                "==",
                username
              )
            );


          const usdtOwnQuery =
            query(
              collection(
                db,
                USDT_COLLECTION
              ),
              where(
                "username",
                "==",
                username
              )
            );


          /* =================================================
             OLD REFERRAL FIELD
          ================================================= */

          const tshReferralByQuery =
            query(
              collection(
                db,
                TSH_COLLECTION
              ),
              where(
                "referralBy",
                "==",
                username
              )
            );


          const visitorReferralByQuery =
            query(
              collection(
                db,
                VISITOR_COLLECTION
              ),
              where(
                "referralBy",
                "==",
                username
              )
            );


          const usdtReferralByQuery =
            query(
              collection(
                db,
                USDT_COLLECTION
              ),
              where(
                "referralBy",
                "==",
                username
              )
            );


          /* =================================================
             NEW REFERRAL RECEIVER FIELD
          ================================================= */

          const tshReferralReceiverQuery =
            query(
              collection(
                db,
                TSH_COLLECTION
              ),
              where(
                "referralBonusReceiver",
                "==",
                username
              )
            );


          const visitorReferralReceiverQuery =
            query(
              collection(
                db,
                VISITOR_COLLECTION
              ),
              where(
                "referralBonusReceiver",
                "==",
                username
              )
            );


          const usdtReferralReceiverQuery =
            query(
              collection(
                db,
                USDT_COLLECTION
              ),
              where(
                "referralBonusReceiver",
                "==",
                username
              )
            );


          /* =================================================
             FIREBASE
          ================================================= */

          const [

            tshOwnSnapshot,

            visitorOwnSnapshot,

            usdtOwnSnapshot,

            tshReferralBySnapshot,

            visitorReferralBySnapshot,

            usdtReferralBySnapshot,

            tshReferralReceiverSnapshot,

            visitorReferralReceiverSnapshot,

            usdtReferralReceiverSnapshot,

          ] = await Promise.all([

            getDocs(
              tshOwnQuery
            ),

            getDocs(
              visitorOwnQuery
            ),

            getDocs(
              usdtOwnQuery
            ),

            getDocs(
              tshReferralByQuery
            ),

            getDocs(
              visitorReferralByQuery
            ),

            getDocs(
              usdtReferralByQuery
            ),

            getDocs(
              tshReferralReceiverQuery
            ),

            getDocs(
              visitorReferralReceiverQuery
            ),

            getDocs(
              usdtReferralReceiverQuery
            ),

          ]);


          /* =================================================
             COMBINE DOCUMENTS
          ================================================= */

          const recordsMap =
            new Map();


          /* =================================================
             HELPER
          ================================================= */

          const addSnapshotToMap =
            (
              snapshot,
              source
            ) => {

              snapshot.docs.forEach(
                document => {

                  recordsMap.set(
                    `${source}-${document.id}`,
                    {

                      id:
                        document.id,

                      source,

                      ...document.data(),

                    }
                  );

                }
              );

            };


          /* =================================================
             OWN RECORDS
          ================================================= */

          addSnapshotToMap(
            tshOwnSnapshot,
            TSH_COLLECTION
          );


          addSnapshotToMap(
            visitorOwnSnapshot,
            VISITOR_COLLECTION
          );


          addSnapshotToMap(
            usdtOwnSnapshot,
            USDT_COLLECTION
          );


          /* =================================================
             REFERRAL BY RECORDS
          ================================================= */

          addSnapshotToMap(
            tshReferralBySnapshot,
            TSH_COLLECTION
          );


          addSnapshotToMap(
            visitorReferralBySnapshot,
            VISITOR_COLLECTION
          );


          addSnapshotToMap(
            usdtReferralBySnapshot,
            USDT_COLLECTION
          );


          /* =================================================
             REFERRAL RECEIVER RECORDS

             THIS IS THE IMPORTANT ADDITION
          ================================================= */

          addSnapshotToMap(
            tshReferralReceiverSnapshot,
            TSH_COLLECTION
          );


          addSnapshotToMap(
            visitorReferralReceiverSnapshot,
            VISITOR_COLLECTION
          );


          addSnapshotToMap(
            usdtReferralReceiverSnapshot,
            USDT_COLLECTION
          );


          const records =
            Array.from(
              recordsMap.values()
            );


          /* =================================================
             SUCCESSFUL RECORDS
          ================================================= */

          const successfulRecords =
            records.filter(
              item => {

                const status =
                  normalizeStatus(
                    item?.status
                  );


                /*
                  Normal successful transaction
                */

                if (
                  status ===
                  "success"
                ) {

                  return true;

                }


                /*
                  Some bonus records may have
                  paid bonus flags even if status
                  is not exactly success.
                */

                if (
                  item?.referralBonusGiven ===
                  true &&
                  normalizeStatus(
                    item?.referralBonusStatus
                  ) ===
                  "paid"
                ) {

                  return true;

                }


                if (
                  item?.selfBonusGiven ===
                  true &&
                  item?.selfBonusApprovedAt
                ) {

                  return true;

                }


                return false;

              }
            );


          /* =================================================
             PREPARE BONUS RECORDS
          ================================================= */

          const preparedRecords = [];


          successfulRecords.forEach(
            item => {

              const transactionUsername =
                normalizeText(
                  item?.username
                );


              /* =================================================
                 A. MY OWN DEPOSIT BONUS
              ================================================= */

              const isMyDeposit =
                transactionUsername ===
                currentUsername;


              if (
                isMyDeposit &&
                item?.selfBonusGiven ===
                true
              ) {

                const bonusAmount =
                  getSelfBonusAmount(
                    item
                  );


                if (
                  bonusAmount > 0
                ) {

                  const currency =
                    item?.selfBonusCurrency ||
                    item?.bonusCurrency ||
                    getTransactionCurrency(
                      item
                    );


                  preparedRecords.push({

                    id:
                    `${item.source}-${item.id}-self`,

                    originalId:
                      item.id,

                    source:
                      item.source,

                    bonusType:
                      "deposit",

                    title:
                      "Deposit Bonus",

                    depositAmount:
                      getDepositAmount(
                        item
                      ),

                    bonusAmount,

                    currency:
                      String(
                        currency ||
                        "TZS"
                      )
                        .trim()
                        .toUpperCase(),

                    timestamp:
                      getTimestampSeconds(
                        item
                      ),

                    status:
                      getBonusStatus(
                        item
                      ),

                  });

                }

              }


              /* =================================================
                 B. MY REFERRAL BONUS

                 CHECK BOTH:

                 referralBonusReceiver
                 referralBy
              ================================================= */

              const isMyReferral =
                isUserReferralReceiver(
                  item,
                  currentUsername
                );


              if (
                isMyReferral &&
                item?.referralBonusGiven ===
                true
              ) {

                const bonusAmount =
                  getReferralBonusAmount(
                    item
                  );


                if (
                  bonusAmount > 0
                ) {

                  const referralCurrency =
                    getReferralBonusCurrency(
                      item
                    );


                  preparedRecords.push({

                    id:
                      `${item.source}-${item.id}-referral`,

                    originalId:
                      item.id,

                    source:
                      item.source,

                    bonusType:
                      "referral",

                    title:
                      "Referral Bonus",

                    /*
                      This is the deposit made
                      by the person referred.
                    */

                    depositAmount:
                      getDepositAmount(
                        item
                      ),

                    bonusAmount,

                    currency:
                      referralCurrency,

                    timestamp:
                      getTimestampSeconds(
                        {
                          ...item,

                          referralBonusClaimedAt:
                            item?.referralBonusClaimedAt ||
                            item?.createdAt,

                        }
                      ),

                    status:
                      getBonusStatus(
                        item
                      ),

                  });

                }

              }

            }
          );


          /* =================================================
             REMOVE DUPLICATES
          ================================================= */

          const uniqueBonusMap =
            new Map();


          preparedRecords.forEach(
            item => {

              uniqueBonusMap.set(
                item.id,
                item
              );

            }
          );


          const uniqueBonusRecords =
            Array.from(
              uniqueBonusMap.values()
            );


          /* =================================================
             SORT NEWEST FIRST
          ================================================= */

          uniqueBonusRecords.sort(
            (
              a,
              b
            ) => {

              return (

                Number(
                  b.timestamp || 0
                ) -

                Number(
                  a.timestamp || 0
                )

              );

            }
          );


          /* =================================================
             SAVE
          ================================================= */

          setBonusList(
            uniqueBonusRecords
          );


          setOpenId(
            null
          );

        }

        catch (
          fetchError
        ) {

          console.error(
            "Bonus history error:",
            fetchError
          );


          setError(
            fetchError?.message ||
            "Imeshindikana kupata historia ya bonus."
          );


          setBonusList([]);

        }

        finally {

          setLoading(
            false
          );

        }

      },
      [
        username,
      ]
    );


  /* =======================================================
     FETCH WHEN USERNAME READY
  ======================================================= */

  useEffect(
    () => {

      if (
        username
      ) {

        fetchBonusHistory();

      }

    },
    [
      username,
      fetchBonusHistory,
    ]
  );


  /* =======================================================
     TOTAL DEPOSIT BONUS
  ======================================================= */

  const totalDepositBonus =
    useMemo(
      () => {

        return bonusList.reduce(
          (
            total,
            item
          ) => {

            if (
              item.bonusType !==
              "deposit"
            ) {

              return total;

            }


            /*
              TZS only for summary.
              USDT is kept separately below.
            */

            if (
              item.currency !==
              "TZS"
            ) {

              return total;

            }


            return (

              total +

              toNumber(
                item.bonusAmount
              )

            );

          },
          0
        );

      },
      [
        bonusList,
      ]
    );


  /* =======================================================
     TOTAL REFERRAL TZS
  ======================================================= */

  const totalReferralBonus =
    useMemo(
      () => {

        return bonusList.reduce(
          (
            total,
            item
          ) => {

            if (
              item.bonusType !==
              "referral"
            ) {

              return total;

            }


            if (
              item.currency !==
              "TZS"
            ) {

              return total;

            }


            return (

              total +

              toNumber(
                item.bonusAmount
              )

            );

          },
          0
        );

      },
      [
        bonusList,
      ]
    );


  /* =======================================================
     TOTAL USDT DEPOSIT BONUS
  ======================================================= */

  const totalUSDTDepositBonus =
    useMemo(
      () => {

        return bonusList.reduce(
          (
            total,
            item
          ) => {

            if (
              item.bonusType !==
              "deposit"
            ) {

              return total;

            }


            if (
              item.currency !==
              "USDT"
            ) {

              return total;

            }


            return (

              total +

              toNumber(
                item.bonusAmount
              )

            );

          },
          0
        );

      },
      [
        bonusList,
      ]
    );


  /* =======================================================
     TOTAL USDT REFERRAL BONUS
  ======================================================= */

  const totalUSDTReferralBonus =
    useMemo(
      () => {

        return bonusList.reduce(
          (
            total,
            item
          ) => {

            if (
              item.bonusType !==
              "referral"
            ) {

              return total;

            }


            if (
              item.currency !==
              "USDT"
            ) {

              return total;

            }


            return (

              total +

              toNumber(
                item.bonusAmount
              )

            );

          },
          0
        );

      },
      [
        bonusList,
      ]
    );


  /* =======================================================
     TOTAL TZS BONUS
  ======================================================= */

  const totalTZSBonus =
    totalDepositBonus +
    totalReferralBonus;


  /* =======================================================
     TOTAL USDT BONUS
  ======================================================= */

  const totalUSDTBonus =
    totalUSDTDepositBonus +
    totalUSDTReferralBonus;


  /* =======================================================
     REFRESH
  ======================================================= */

  const handleRefresh =
    async () => {

      const freshUsername =
        getUsernameFromLocalStorage();


      if (
        normalizeText(
          freshUsername
        ) !==
        normalizeText(
          username
        )
      ) {

        setUsername(
          freshUsername
        );

        return;

      }


      await fetchBonusHistory();

    };


  /* =======================================================
     TOGGLE DETAILS
  ======================================================= */

  const toggleDetails =
    (
      id
    ) => {

      setOpenId(
        previous => {

          if (
            previous ===
            id
          ) {

            return null;

          }


          return id;

        }
      );

    };


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="user-bonus-history">


      {/* ===================================================
          HEADER
      =================================================== */}

      <div className="user-bonus-topbar">

        <div className="
          user-bonus-title-wrap
        ">

          <div className="
            user-bonus-title-icon
          ">

            🎁

          </div>


          <div>

            <h2>
              Bonus Center
            </h2>

            <p>
              Historia ya bonus zako
            </p>

          </div>

        </div>


        <button
          type="button"
          className="
            user-bonus-refresh
          "
          onClick={
            handleRefresh
          }
          disabled={
            loading
          }
        >

          {
            loading
              ? "Loading..."
              : "↻ Refresh"
          }

        </button>

      </div>


      {/* ===================================================
          USER IDENTITY
      =================================================== */}

      <div className="
        user-bonus-identity
      ">

        <div className="
          user-bonus-avatar
        ">

          {
            String(
              username || "U"
            )
              .charAt(0)
              .toUpperCase()
          }

        </div>


        <div className="
          user-bonus-user-info
        ">

          <span>
            USERNAME
          </span>

          <strong>
            {username || "-"}
          </strong>

        </div>


        <div className="
          user-bonus-secure
        ">

          <span className="
            secure-dot
          " />

          Bonus Center

        </div>

      </div>


      {/* ===================================================
          SUMMARY
      =================================================== */}

      <div className="
        user-bonus-summary
      ">


        {/* TZS DEPOSIT BONUS */}

        <div className="
          user-bonus-summary-card
        ">

          <div className="
            summary-card-icon
          ">

            💰

          </div>


          <div>

            <span>
              Deposit Bonus
            </span>

            <strong>
              {formatMoney(
                totalDepositBonus,
                "TZS"
              )}
            </strong>

          </div>

        </div>


        {/* TZS REFERRAL BONUS */}

        <div className="
          user-bonus-summary-card
        ">

          <div className="
            summary-card-icon
          ">

            👥

          </div>


          <div>

            <span>
              Referral Bonus
            </span>

            <strong>
              {formatMoney(
                totalReferralBonus,
                "TZS"
              )}
            </strong>

          </div>

        </div>


        {/* TZS TOTAL */}

        <div className="
          user-bonus-summary-card
          total
        ">

          <div className="
            summary-card-icon
          ">

            ✨

          </div>


          <div>

            <span>
              Total TZS Bonus
            </span>

            <strong>
              {formatMoney(
                totalTZSBonus,
                "TZS"
              )}
            </strong>

          </div>

        </div>


        {/* RECORDS */}

        <div className="
          user-bonus-summary-card
        ">

          <div className="
            summary-card-icon
          ">

            📊

          </div>


          <div>

            <span>
              Records
            </span>

            <strong>
              {bonusList.length}
            </strong>

          </div>

        </div>

      </div>


      {/* ===================================================
          USDT SUMMARY
      =================================================== */}

      {totalUSDTBonus > 0 && (

        <div className="
          user-bonus-summary
        ">


          <div className="
            user-bonus-summary-card
          ">

            <div className="
              summary-card-icon
            ">

              💵

            </div>


            <div>

              <span>
                USDT Deposit Bonus
              </span>

              <strong>
                {formatMoney(
                  totalUSDTDepositBonus,
                  "USDT"
                )}
              </strong>

            </div>

          </div>


          <div className="
            user-bonus-summary-card
          ">

            <div className="
              summary-card-icon
            ">

              👥

            </div>


            <div>

              <span>
                USDT Referral Bonus
              </span>

              <strong>
                {formatMoney(
                  totalUSDTReferralBonus,
                  "USDT"
                )}
              </strong>

            </div>

          </div>


          <div className="
            user-bonus-summary-card
            total
          ">

            <div className="
              summary-card-icon
            ">

              ✨

            </div>


            <div>

              <span>
                Total USDT Bonus
              </span>

              <strong>
                {formatMoney(
                  totalUSDTBonus,
                  "USDT"
                )}
              </strong>

            </div>

          </div>

        </div>

      )}


      {/* ===================================================
          ERROR
      =================================================== */}

      {error && (

        <div className="
          user-bonus-error
        ">

          ⚠️ {error}

        </div>

      )}


      {/* ===================================================
          LOADING
      =================================================== */}

      {loading ? (

        <div className="
          user-bonus-loading
        ">

          <div className="
            user-bonus-spinner
          " />

          <span>
            Inapakia bonus history...
          </span>

        </div>

      ) : bonusList.length === 0 ? (

        <div className="
          user-bonus-empty
        ">

          <div className="
            user-bonus-empty-icon
          ">

            🎁

          </div>


          <h3>
            Hakuna bonus bado
          </h3>


          <p>
            Bonus zako za deposit na
            referral zitaonekana hapa
            baada ya kulipwa.
          </p>

        </div>

      ) : (

        <>


          {/* =================================================
              SECTION HEADER
          ================================================= */}

          <div className="
            user-bonus-section-header
          ">

            <div>

              <h3>
                Bonus Transactions
              </h3>

              <p>
                Bonus zako za deposit na
                watu uliowaalika
              </p>

            </div>


            <div className="
              user-bonus-count
            ">

              {bonusList.length}

              {" Records"}

            </div>

          </div>


          {/* =================================================
              DESKTOP TABLE
          ================================================= */}

          <div className="
            user-bonus-table-wrapper
          ">

            <table className="
              user-bonus-table
            ">

              <thead>

                <tr>

                  <th>
                    Date & Time
                  </th>

                  <th>
                    Bonus
                  </th>

                  <th>
                    Amount
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Details
                  </th>

                </tr>

              </thead>


              <tbody>

                {bonusList.map(
                  item => {

                    const isOpen =
                      openId ===
                      item.id;


                    const isDeposit =
                      item.bonusType ===
                      "deposit";


                    const isUSDT =
                      item.source ===
                      USDT_COLLECTION ||
                      item.currency ===
                      "USDT";


                    return (

                      <React.Fragment
                        key={
                          item.id
                        }
                      >


                        {/* MAIN ROW */}

                        <tr
                          className={
                            isOpen
                              ? "bonus-main-row-open"
                              : ""
                          }
                        >


                          {/* DATE */}

                          <td>

                            <span className="
                              bonus-date
                            ">

                              {formatDate(
                                item.timestamp
                              )}

                            </span>

                          </td>


                          {/* BONUS TYPE */}

                          <td>

                            <span
                              className={`
                                bonus-type-pill
                                ${
                                  isDeposit
                                    ? "self"
                                    : "referral"
                                }
                              `}
                            >

                              {
                                isDeposit
                                  ? "💰 Deposit Bonus"
                                  : "👥 Referral Bonus"
                              }

                            </span>


                            {isUSDT && (

                              <span
                                style={{
                                  marginLeft:
                                    "8px",
                                  fontSize:
                                    "11px",
                                  fontWeight:
                                    "700",
                                  opacity:
                                    0.75,
                                }}
                              >

                                USDT

                              </span>

                            )}

                          </td>


                          {/* AMOUNT */}

                          <td>

                            <strong className="
                              bonus-amount
                            ">

                              +

                              {formatMoney(
                                item.bonusAmount,
                                item.currency
                              )}

                            </strong>

                          </td>


                          {/* STATUS */}

                          <td>

                            <span className="
                              status-paid
                            ">

                              ✓ {item.status}

                            </span>

                          </td>


                          {/* DETAILS */}

                          <td>

                            <button
                              type="button"
                              className={`
                                bonus-details-btn
                                ${
                                  isOpen
                                    ? "open"
                                    : ""
                                }
                              `}
                              onClick={() =>
                                toggleDetails(
                                  item.id
                                )
                              }
                              aria-expanded={
                                isOpen
                              }
                              aria-label={
                                isOpen
                                  ? "Close details"
                                  : "View details"
                              }
                            >

                              <span>

                                {
                                  isOpen
                                    ? "⌄"
                                    : ">"
                                }

                              </span>

                            </button>

                          </td>

                        </tr>


                        {/* DETAILS */}

                        {isOpen && (

                          <tr className="
                            bonus-details-row
                          ">

                            <td
                              colSpan="5"
                            >

                              <div className="
                                bonus-details-panel
                              ">


                                {/* HEADER */}

                                <div className="
                                  bonus-detail-title
                                ">

                                  <div>

                                    <span>
                                      BONUS DETAILS
                                    </span>

                                    <strong>

                                      {
                                        isDeposit
                                          ? "Deposit Bonus"
                                          : "Referral Bonus"
                                      }

                                    </strong>

                                  </div>


                                  <span className="
                                    detail-paid
                                  ">

                                    ✓ {item.status}

                                  </span>

                                </div>


                                {/* GRID */}

                                <div className="
                                  bonus-detail-grid
                                ">


                                  {/* BONUS */}

                                  <div className="
                                    bonus-detail-item
                                  ">

                                    <span>
                                      Bonus Amount
                                    </span>

                                    <strong className="
                                      bonus-amount
                                    ">

                                      +

                                      {formatMoney(
                                        item.bonusAmount,
                                        item.currency
                                      )}

                                    </strong>

                                  </div>


                                  {/* DEPOSIT */}

                                  <div className="
                                    bonus-detail-item
                                  ">

                                    <span>
                                      Deposit Amount
                                    </span>

                                    <strong>

                                      {formatMoney(
                                        item.depositAmount,
                                        item.currency
                                      )}

                                    </strong>

                                  </div>


                                  {/* DATE */}

                                  <div className="
                                    bonus-detail-item
                                  ">

                                    <span>
                                      Date & Time
                                    </span>

                                    <strong>

                                      {formatDate(
                                        item.timestamp
                                      )}

                                    </strong>

                                  </div>


                                  {/* STATUS */}

                                  <div className="
                                    bonus-detail-item
                                  ">

                                    <span>
                                      Status
                                    </span>

                                    <strong className="
                                      detail-paid
                                    ">

                                      ✓ {item.status}

                                    </strong>

                                  </div>


                                  {/* CURRENCY */}

                                  <div className="
                                    bonus-detail-item
                                  ">

                                    <span>
                                      Currency
                                    </span>

                                    <strong>

                                      {item.currency}

                                    </strong>

                                  </div>


                                  {/* TYPE */}

                                  <div className="
                                    bonus-detail-item
                                  ">

                                    <span>
                                      Bonus Type
                                    </span>

                                    <strong>

                                      {
                                        isDeposit
                                          ? "First Deposit 10%"
                                          : "Referral 8%"
                                      }

                                    </strong>

                                  </div>

                                </div>


                                {/* PRIVATE NOTE */}

                                <div className="
                                  bonus-private-note
                                ">

                                  <span>
                                    🔒
                                  </span>

                                  <p>
                                    Taarifa hii ni
                                    ya bonus yako.
                                    Taarifa za mtu
                                    mwingine
                                    hazionyeshwi.
                                  </p>

                                </div>

                              </div>

                            </td>

                          </tr>

                        )}

                      </React.Fragment>

                    );

                  }
                )}

              </tbody>

            </table>

          </div>


          {/* =================================================
              MOBILE
          ================================================= */}

          <div className="
            user-bonus-mobile-list
          ">

            {bonusList.map(
              item => {

                const isOpen =
                  openId ===
                  item.id;


                const isDeposit =
                  item.bonusType ===
                  "deposit";


                const isUSDT =
                  item.source ===
                  USDT_COLLECTION ||
                  item.currency ===
                  "USDT";


                return (

                  <div
                    className={`
                      user-bonus-card
                      ${
                        isOpen
                          ? "open"
                          : ""
                      }
                    `}
                    key={
                      `mobile-${item.id}`
                    }
                  >


                    {/* TOP */}

                    <button
                      type="button"
                      className="
                        user-bonus-card-top
                      "
                      onClick={() =>
                        toggleDetails(
                          item.id
                        )
                      }
                      aria-expanded={
                        isOpen
                      }
                    >

                      <div className="
                        user-bonus-card-summary
                      ">

                        <span className="
                          mobile-card-label
                        ">

                          {
                            isDeposit
                              ? "DEPOSIT BONUS"
                              : "REFERRAL BONUS"
                          }

                        </span>


                        <strong className="
                          mobile-card-amount
                        ">

                          +

                          {formatMoney(
                            item.bonusAmount,
                            item.currency
                          )}

                        </strong>


                        <span className="
                          mobile-card-date
                        ">

                          {formatDate(
                            item.timestamp
                          )}

                        </span>


                        {isUSDT && (

                          <span
                            style={{
                              fontSize:
                                "11px",
                              fontWeight:
                                "700",
                              marginTop:
                                "3px",
                              opacity:
                                0.75,
                            }}
                          >

                            USDT Transaction

                          </span>

                        )}

                      </div>


                      <div className="
                        mobile-card-right
                      ">

                        <span className="
                          status-paid
                        ">

                          ✓ {item.status}

                        </span>


                        <span className={`
                          mobile-details-btn
                          ${
                            isOpen
                              ? "open"
                              : ""
                          }
                        `}>

                          {
                            isOpen
                              ? "⌄"
                              : ">"
                          }

                        </span>

                      </div>

                    </button>


                    {/* DETAILS */}

                    {isOpen && (

                      <div className="
                        mobile-bonus-details
                      ">


                        {/* TITLE */}

                        <div className="
                          mobile-details-title
                        ">

                          <div>

                            <span>
                              BONUS DETAILS
                            </span>

                            <strong>

                              {
                                isDeposit
                                  ? "Deposit Bonus"
                                  : "Referral Bonus"
                              }

                            </strong>

                          </div>


                          <span className="
                            detail-paid
                          ">

                            ✓ {item.status}

                          </span>

                        </div>


                        {/* BONUS */}

                        <div className="
                          user-bonus-card-row
                        ">

                          <span>
                            Bonus Amount
                          </span>

                          <strong className="
                            bonus-amount
                          ">

                            +

                            {formatMoney(
                              item.bonusAmount,
                              item.currency
                            )}

                          </strong>

                        </div>


                        {/* DEPOSIT */}

                        <div className="
                          user-bonus-card-row
                        ">

                          <span>
                            Deposit Amount
                          </span>

                          <strong>

                            {formatMoney(
                              item.depositAmount,
                              item.currency
                            )}

                          </strong>

                        </div>


                        {/* DATE */}

                        <div className="
                          user-bonus-card-row
                        ">

                          <span>
                            Date & Time
                          </span>

                          <strong>

                            {formatDate(
                              item.timestamp
                            )}

                          </strong>

                        </div>


                        {/* STATUS */}

                        <div className="
                          user-bonus-card-row
                        ">

                          <span>
                            Status
                          </span>

                          <strong className="
                            status-paid
                          ">

                            ✓ {item.status}

                          </strong>

                        </div>


                        {/* CURRENCY */}

                        <div className="
                          user-bonus-card-row
                        ">

                          <span>
                            Currency
                          </span>

                          <strong>

                            {item.currency}

                          </strong>

                        </div>


                        {/* BONUS LABEL */}

                        <div className="
                          user-bonus-card-row
                        ">

                          <span>
                            Bonus
                          </span>

                          <strong>

                            {
                              isDeposit
                                ? "First Deposit 10%"
                                : "Referral 8%"
                            }

                          </strong>

                        </div>


                        {/* SOURCE */}

                        <div className="
                          user-bonus-card-row
                        ">

                          <span>
                            Source
                          </span>

                          <strong>

                            {
                              isUSDT
                                ? "USDT"
                                : item.source ===
                                  TSH_COLLECTION
                                  ? "TSH"
                                  : "Visitor"
                            }

                          </strong>

                        </div>


                        {/* PRIVATE NOTE */}

                        <div className="
                          bonus-private-note
                        ">

                          <span>
                            🔒
                          </span>

                          <p>
                            Bonus hii ni
                            taarifa yako
                            binafsi.
                          </p>

                        </div>

                      </div>

                    )}

                  </div>

                );

              }
            )}

          </div>

        </>

      )}

    </div>

  );

}
